import { useTheme, spacing } from '@fti/design-system';

interface StatusBadgeProps {
  status: string;
  size?: 'sm' | 'md';
}

const STATUS_COLORS: Record<string, { bgKey: string; label: string }> = {
  draft: { bgKey: 'warning', label: 'Draft' },
  registered: { bgKey: 'info', label: 'Registered' },
  active: { bgKey: 'success', label: 'Active' },
  archived: { bgKey: 'neutral', label: 'Archived' },
  pending: { bgKey: 'neutral', label: 'Pending' },
  running: { bgKey: 'info', label: 'Running' },
  completed: { bgKey: 'success', label: 'Completed' },
  failed: { bgKey: 'error', label: 'Failed' },
  rejected: { bgKey: 'error', label: 'Rejected' },
  sample_running: { bgKey: 'info', label: 'Sample Running' },
  sample_completed: { bgKey: 'success', label: 'Sample Done' },
  full_running: { bgKey: 'info', label: 'Full Running' },
  full_completed: { bgKey: 'success', label: 'Full Done' },
  // Publication reflection statuses
  not_generated: { bgKey: 'neutral', label: 'Not Generated' },
  generating: { bgKey: 'info', label: 'Generating' },
  current: { bgKey: 'success', label: 'Current' },
  // Simulation agent stages
  journalist_planning: { bgKey: 'info', label: 'Planning' },
  journalist_writing: { bgKey: 'info', label: 'Writing' },
  headline_generation: { bgKey: 'info', label: 'Headline' },
  editor_review: { bgKey: 'info', label: 'Editor Review' },
  // Editor decisions
  accepted: { bgKey: 'success', label: 'Accepted' },
};

export function StatusBadge({ status, size = 'sm' }: StatusBadgeProps) {
  const { theme } = useTheme();
  const config = STATUS_COLORS[status] || { bgKey: 'neutral', label: status };

  const isNeutral = config.bgKey === 'neutral';
  // Neutral (Pending / Not Generated / Archived) uses a soft light-grey chip
  // with dark text instead of a harsh near-black fill.
  const bgColor = isNeutral
    ? theme.colors.neutral[200]
    : theme.colors.semantic[config.bgKey as 'warning' | 'info' | 'success' | 'error']?.default || theme.colors.neutral[200];

  const textColor = isNeutral ? theme.colors.text.secondary : theme.colors.text.inverse;

  return (
    <span
      style={{
        display: 'inline-block',
        padding: size === 'sm' ? `2px ${spacing.xs}` : `${spacing.xs} ${spacing.sm}`,
        fontSize: size === 'sm' ? '0.7rem' : '0.8rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        borderRadius: '4px',
        backgroundColor: bgColor,
        color: textColor,
        lineHeight: 1.4,
      }}
    >
      {config.label}
    </span>
  );
}
